/** React imports */
import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useContext,
  useEffect,
  lazy,
} from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
/** App imports */
import { routeConfig, RouteConfigInterface } from './routerConfig';
import AppScopeLoadingComponent from '../utils/components/AppScopeLoading';
import ErrorBoundary from '../utils/components/ErrorBoundary';
import { Layout } from '../Components/Root/Layout';
import { appContext } from '../Contexts/AppContext';
import CompanyValidator from '../utils/components/CompanyValidator';
import ProjectValidator from '../utils/components/ProjectValidator';

const PrintPage = lazy(() => import('../Components/Pages/Print'));
const PrintV3 = lazy(() => import('../Components/Pages/PrintV3'));

interface AppRouterProps {
  isDrawerOpen: boolean;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  signOut: () => void;
}

const AppRouter: React.FC<AppRouterProps> = (
  props: AppRouterProps,
): JSX.Element => {
  const { user } = useContext(appContext);

  /**
   * Create Route component from config object
   *
   * Adds:
   * - Suspense (for lazy loading)
   * - project validation (optionally)
   */
  const createRoute = (config: RouteConfigInterface): JSX.Element => {
    /**
     * Recursively nest Component from config into validator components
     */
    const nestValidators = (
      validators: FunctionComponent<any>[],
    ): JSX.Element => {
      const validator: FunctionComponent<any> =
        validators.shift() as FunctionComponent<any>;
      if (validators.length > 0) {
        return React.createElement(
          validator,
          { user, navItemId: config.id },
          nestValidators([...validators]),
        );
      } else {
        return React.createElement(
          validator,
          { user, navItemId: config.id },
          config.component,
        );
      }
    };
    const element: JSX.Element = React.createElement(
      React.Suspense,
      { fallback: <AppScopeLoadingComponent /> },
      config.validators.length > 0
        ? nestValidators([...config.validators])
        : config.component,
    );
    return (
      <Route path={config.path} element={element} key={`${config.id}-route`} />
    );
  };

  // Store last accessed route in Session Storage
  const location = useLocation();
  useEffect(() => {
    if (!window.location.href.endsWith('/redirect')) {
      window.sessionStorage.setItem('last-visited-route', window.location.href);
    }
  }, [location]);

  return (
    <ErrorBoundary>
      <Routes>
        {/* Print route needs to be outside layout */}
        <Route
          path={'/:companyId/projects/:projectId/print'}
          element={
            <CompanyValidator user={user} navItemId="print-route">
              <ProjectValidator user={user} navItemId="print-route">
                <PrintPage />
              </ProjectValidator>
            </CompanyValidator>
          }
        />
        <Route
          path={'/:companyId/projects/:projectId/print-v3'}
          element={
            <CompanyValidator user={user} navItemId={'print-route-3'}>
              <ProjectValidator user={user} navItemId={'print-route-3'}>
                <PrintV3 />
              </ProjectValidator>
            </CompanyValidator>
          }
        />
        <Route element={<Layout {...props} />}>
          {routeConfig.map((route: RouteConfigInterface) => {
            return createRoute(route);
          })}
        </Route>
      </Routes>
    </ErrorBoundary>
  );
};

export default AppRouter;
